import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Lightbulb as SuggestionIcon,
  Assessment as ReportIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { exportPerformanceReportPdf } from '../utils/exportPdf';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { testRunsApi } from '../services/api';

const COLORS = ['#4caf50', '#2196f3', '#ff9800', '#f44336'];

function TestRunDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [testRun, setTestRun] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadData();
    // Poll for updates if running
    const interval = setInterval(() => {
      if (testRun?.status === 'running' || testRun?.status === 'pending') {
        loadTestRun();
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [id, testRun?.status]);

  const loadData = async () => {
    try {
      await loadTestRun();
    } finally {
      setLoading(false);
    }
  };

  const loadTestRun = async () => {
    try {
      const response = await testRunsApi.get(id);
      setTestRun(response.data);
      
      if (response.data.status === 'completed') {
        try {
          const reportRes = await testRunsApi.getReport(id);
          setReport(reportRes.data);
        } catch (error) {
          console.error('Error loading report:', error);
        }
      }
    } catch (error) {
      console.error('Error loading test run:', error);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#4caf50';
    if (score >= 70) return '#2196f3';
    if (score >= 50) return '#ff9800';
    return '#f44336';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excelente';
    if (score >= 70) return 'Bom';
    if (score >= 50) return 'Regular';
    return 'Precisa Melhorar';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!testRun) {
    return (
      <Box>
        <Alert severity="error">Teste não encontrado</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/test-runs')} sx={{ mt: 2 }}>
          Voltar
        </Button>
      </Box>
    );
  }

  const radarData = report?.metrics
    ? [
        { metric: 'Assertividade', value: report.metrics.assertiveness?.average || 0 },
        { metric: 'Coerência', value: report.metrics.coherence?.average || 0 },
        { metric: 'Relevância', value: report.metrics.relevance?.average || 0 },
        { metric: 'Completude', value: report.metrics.completeness?.average || 0 },
      ]
    : [];

  const distributionData = report?.score_distribution
    ? Object.entries(report.score_distribution).map(([name, value]) => ({
        name: name.split(' ')[0],
        value,
      }))
    : [];

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/test-runs')}>
          <BackIcon />
        </IconButton>
        <Box flexGrow={1}>
          <Typography variant="h4">{testRun.name}</Typography>
          <Typography color="textSecondary">
            {testRun.status === 'completed'
              ? `Concluído em ${new Date(testRun.completed_at).toLocaleString('pt-BR')}`
              : testRun.status === 'running'
              ? 'Em execução...'
              : testRun.status}
          </Typography>
        </Box>
        <Chip
          label={testRun.status}
          color={
            testRun.status === 'completed'
              ? 'success'
              : testRun.status === 'running'
              ? 'warning'
              : testRun.status === 'failed'
              ? 'error'
              : 'default'
          }
        />
        {testRun.status === 'completed' && (
          <Button
            variant="contained"
            color="error"
            startIcon={<PdfIcon />}
            onClick={() => exportPerformanceReportPdf(testRun, report)}
            sx={{ ml: 1 }}
          >
            Exportar PDF
          </Button>
        )}
        {testRun.overall_score !== null && (
          <Box textAlign="center">
            <Typography
              variant="h3"
              sx={{ color: getScoreColor(testRun.overall_score) }}
            >
              {testRun.overall_score.toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {getScoreLabel(testRun.overall_score)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Progress for running tests */}
      {testRun.status === 'running' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Progresso do Teste
          </Typography>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography>
              {testRun.completed_questions} de {testRun.total_questions} perguntas processadas
            </Typography>
            <Typography>
              {Math.round((testRun.completed_questions / testRun.total_questions) * 100)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(testRun.completed_questions / testRun.total_questions) * 100}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Paper>
      )}

      {/* Completed test content */}
      {testRun.status === 'completed' && (
        <>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
            <Tab label="Visão Geral" icon={<ReportIcon />} iconPosition="start" />
            <Tab label="Resultados Detalhados" />
            <Tab label="Sugestões de Melhoria" icon={<SuggestionIcon />} iconPosition="start" />
          </Tabs>

          {/* Overview Tab */}
          {tabValue === 0 && (
            <Grid container spacing={3}>
              {/* Metrics Cards */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Métricas por Dimensão
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="metric" />
                      <PolarRadiusAxis domain={[0, 100]} />
                      <Radar
                        name="Score"
                        dataKey="value"
                        stroke="#1976d2"
                        fill="#1976d2"
                        fillOpacity={0.5}
                      />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Distribuição de Scores
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={distributionData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => (value > 0 ? `${name}: ${value}` : '')}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {distributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Report Summary */}
              {testRun.report && (
                <>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Resumo Executivo
                      </Typography>
                      <Typography paragraph>
                        {testRun.report.executive_summary}
                      </Typography>
                      <Chip
                        label={`Avaliação: ${testRun.report.overall_assessment}`}
                        color={
                          testRun.report.overall_assessment === 'excellent'
                            ? 'success'
                            : testRun.report.overall_assessment === 'good'
                            ? 'info'
                            : testRun.report.overall_assessment === 'needs_improvement'
                            ? 'warning'
                            : 'error'
                        }
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                      <Typography variant="h6" gutterBottom color="success.main">
                        <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Pontos Fortes
                      </Typography>
                      <List>
                        {testRun.report.strengths?.map((strength, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <SuccessIcon color="success" />
                            </ListItemIcon>
                            <ListItemText primary={strength} />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                      <Typography variant="h6" gutterBottom color="warning.main">
                        <TrendingDownIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Áreas para Melhoria
                      </Typography>
                      <List>
                        {testRun.report.areas_for_improvement?.map((area, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <ErrorIcon color="warning" />
                            </ListItemIcon>
                            <ListItemText
                              primary={area.area || area}
                              secondary={area.description}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {/* Detailed Results Tab */}
          {tabValue === 1 && (
            <Box>
              {testRun.results?.map((result, index) => (
                <Accordion key={result.id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={2} width="100%">
                      <Chip label={index + 1} size="small" />
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: getScoreColor(result.overall_score || 0),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      >
                        {result.overall_score?.toFixed(0) || 0}
                      </Box>
                      <Typography noWrap sx={{ flexGrow: 1 }}>
                        {result.question_text}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {result.response_time_ms}ms
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Pergunta:
                        </Typography>
                        <Typography paragraph>{result.question_text}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Resposta Esperada:
                        </Typography>
                        <Typography paragraph>
                          {result.expected_answer || 'Não definida'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Resposta do Agente:
                        </Typography>
                        <Typography paragraph>
                          {result.agent_response || 'Sem resposta'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          Scores:
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <Chip
                            label={`Assertividade: ${result.assertiveness_score?.toFixed(0) || 0}`}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={`Coerência: ${result.coherence_score?.toFixed(0) || 0}`}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={`Relevância: ${result.relevance_score?.toFixed(0) || 0}`}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={`Completude: ${result.completeness_score?.toFixed(0) || 0}`}
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </Grid>
                      {result.feedback && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Feedback:
                          </Typography>
                          <Typography>{result.feedback}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {/* Improvement Suggestions Tab */}
          {tabValue === 2 && testRun.report && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Recomendações de Melhoria
                  </Typography>
                  {testRun.report.recommendations?.map((rec, index) => (
                    <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={2} mb={1}>
                          <SuggestionIcon color="primary" />
                          <Typography variant="subtitle1">
                            {rec.title || `Recomendação ${index + 1}`}
                          </Typography>
                          {rec.expected_impact && (
                            <Chip
                              label={`Impacto: ${rec.expected_impact}`}
                              size="small"
                              color={
                                rec.expected_impact === 'high'
                                  ? 'error'
                                  : rec.expected_impact === 'medium'
                                  ? 'warning'
                                  : 'info'
                              }
                            />
                          )}
                        </Box>
                        <Typography color="textSecondary">
                          {rec.description || rec}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Conclusão
                  </Typography>
                  <Typography>{testRun.report.conclusion}</Typography>
                </Paper>
              </Grid>

              {report?.improvement_suggestions?.length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Ações Sugeridas
                    </Typography>
                    <List>
                      {report.improvement_suggestions.map((suggestion, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <SuggestionIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={suggestion} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </>
      )}

      {/* Failed test */}
      {testRun.status === 'failed' && (
        <Alert severity="error">
          <Typography variant="h6">Teste Falhou</Typography>
          <Typography>
            {testRun.report?.error || 'Ocorreu um erro durante a execução do teste.'}
          </Typography>
        </Alert>
      )}
    </Box>
  );
}

export default TestRunDetail;
